import {
  Button,
  chakra,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useToast,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { EsgLabel, FormCheckBox, FormSelect, FormTextArea } from "../../../../../components/Forms";
import { dimensionsKeys, generateArrayOfYears, QUESTIONNAIRE_STATUSES, showErrorToast } from "../../../../../utils";
import { useForm, Controller } from "react-hook-form";
import { useGetOrganizationStructure, useGetOrganizationUsers } from "../../../../../hooks/query";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { hideAppSpinner, showAppSpinner } from "../../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useGetAssigneeTemplates, useGetDimensions, useGetEntityAppropriateDimensionItems } from "../../../../../hooks/query/dimensions";
import { MultiSelect } from "../../../../../components";
import { prettyPrintCodeName } from "../../../../../utils/text-utils";
import EsgFormLabel from "../../../../../components/Forms/EsgFormLabel";

const ALL_VALUE = "ALL";

function mapDataToFormData(data) {
  const assigneeId = data?.assigneeId ?? "";
  return {
    id: data?.id,
    description: data?.description ?? "",
    dimensionOne: data?.dimensionItems?.dimensionOneItem ?? ALL_VALUE,
    dimensionTwo: data?.dimensionItems?.dimensionTwoItem ?? ALL_VALUE,
    dimensionThree: data?.dimensionItems?.dimensionThreeItem ?? ALL_VALUE,
    dimensionFour: data?.dimensionItems?.dimensionFourItem ?? ALL_VALUE,
    dimensionFive: data?.dimensionItems?.dimensionFiveItem ?? ALL_VALUE,
    assignee: assigneeId,
    year: data?.period?.year ?? "",
    quarter: data?.period?.quarter ? { label: data?.period?.quarter, value: data.period.quarter } : null,
    directEmissionsShouldBeFilled: data?.configurations?.directEmissionsShouldBeFilled ?? false,
    indirectEmissionsShouldBeFilled: data?.configurations?.indirectEmissionsShouldBeFilled ?? false,
    purchasedGoodsAndServicesShouldBeFilled: data?.configurations?.purchasedGoodsAndServicesShouldBeFilled ?? false,
    capitalGoodsShouldBeFilled: data?.configurations?.capitalGoodsShouldBeFilled ?? false,
    fuelAndEnergyRelatedActivitiesShouldBeFilled:
      data?.configurations?.fuelAndEnergyRelatedActivitiesShouldBeFilled ?? false,
    upstreamTransportationAndDistributionShouldBeFilled:
      data?.configurations?.upstreamTransportationAndDistributionShouldBeFilled ?? false,
    wasteGeneratedInOperationsShouldBeFilled: data?.configurations?.wasteGeneratedInOperationsShouldBeFilled ?? false,
    businessTravelShouldBeFilled: data?.configurations?.businessTravelShouldBeFilled ?? false,
    employeeCommutingShouldBeFilled: data?.configurations?.employeeCommutingShouldBeFilled ?? false,
    upstreamLeasedAssetsShouldBeFilled: data?.configurations?.upstreamLeasedAssetsShouldBeFilled ?? false,
    downstreamTransportationAndDistributionShouldBeFilled:
      data?.configurations?.downstreamTransportationAndDistributionShouldBeFilled ?? false,
    processingOfSoldProductsShouldBeFilled: data?.configurations?.processingOfSoldProductsShouldBeFilled ?? false,
    useOfSoldProductsShouldBeFilled: data?.configurations?.useOfSoldProductsShouldBeFilled ?? false,
    treatmentOfSoldProductsShouldBeFilled: data?.configurations?.treatmentOfSoldProductsShouldBeFilled ?? false,
    downstreamLeasedAssetsShouldBeFilled: data?.configurations?.downstreamLeasedAssetsShouldBeFilled ?? false,
    franchisesShouldBeFilled: data?.configurations?.franchisesShouldBeFilled ?? false,
    investmentsShouldBeFilled: data?.configurations?.investmentsShouldBeFilled ?? false,
  };
}

function DataEntryPlanningModal({ modalData, isOpen, onClose, onSubmit }) {
  const toast = useToast();
  const dispatch = useDispatch();
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning",
  });

  const [useTemplate, setUseTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm({});

  const { data: organizationStructure } = useGetOrganizationStructure();

  useGetAssigneeTemplates({
    onSuccess: (data) => {
      setTemplates(data);
    },
    cacheTime: 0,
  });

  const formValues = watch();

  const getDimensionsQueryParams = () => {
    const queryParams = {};
    dimensionsKeys.forEach((key) => {
      if (typeof formValues[key] !== "undefined" && formValues[key] !== ALL_VALUE) {
        queryParams[`${key}Item`] = formValues[key];
      }
    });
    return queryParams;
  };

  const { data: dimensionsData } = useGetDimensions(getDimensionsQueryParams(), {
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  // NO NEED, SINCE WE ARE NOW ABLE TO FETCH THE STATE PARAMETER INSIDE THE DIMENSION QUERY ABOVE.
  // const { data: dimensionsItemData } = useGetEntityAppropriateDimensionItems({
  //   onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  //   cacheTime: 0,
  // });

  // console.log('dimensionItem Data => ',dimensionsItemData);

  useEffect(() => {
    if (isOpen) {
      if (modalData) {
        // Editing existing task
        reset(mapDataToFormData(modalData));
      } else {
        // Creating new task
        reset(mapDataToFormData());
      }
      // Reset template state
      setUseTemplate(false);
    }
  }, [isOpen, modalData, reset]);

  // Handle template selection
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;

    if (templateId) {
      const template = templates.find((t) => t.id === Number(templateId) || t.id === templateId);

      if (template) {
        // Prefill form fields with template data
        setValue("dimensionOne", template?.dimensionItems?.dimensionOneItem || ALL_VALUE);
        setValue("dimensionTwo", template?.dimensionItems?.dimensionTwoItem || ALL_VALUE);
        setValue("dimensionThree", template?.dimensionItems?.dimensionThreeItem || ALL_VALUE);
        setValue("dimensionFour", template?.dimensionItems?.dimensionFourItem || ALL_VALUE);
        setValue("dimensionFive", template?.dimensionItems?.dimensionFiveItem || ALL_VALUE);
        setValue("assignee", template.assigneeId);
      }
    }
  };

  const { data: users, isLoading: isUsersLoading } = useGetOrganizationUsers({
    onError: (err) => {
      showErrorToast(toast, err.message);
      handleModalClose();
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
  });

  useEffect(() => {
    if (isUsersLoading) {
      dispatch(showAppSpinner());
    }
  }, [dispatch, isUsersLoading]);

  const prepareSubmitData = (data) => {
    // if modalData is not null, it means we are editing an existing task, and will be only submitting for the selected quarter,
    const quarters = modalData?.id ? [data.quarter] : data.quarter;
    // for multiple quarters, submit for each quarter
    if (quarters && quarters.length > 0) {
      quarters.forEach((quarter) => {
        const dimensionThreeName = organizationStructure?.dimensions["dimensionThree"]?.items?.find(
          (d) => d?.id === data?.dimensionThree
        )?.name;
        // if dimensionThree is selected and useTemplate is true, append the name to the description
        const description =
          data.dimensionThree && useTemplate ? `${data.description} - ${dimensionThreeName}` : data.description;
        const dataToSave = {
          description: description,
          dimensionItems: {
            dimensionOneItem:
              data.dimensionOne !== undefined && data.dimensionOne !== ALL_VALUE ? data.dimensionOne : null,
            dimensionTwoItem:
              data.dimensionTwo !== undefined && data.dimensionTwo !== ALL_VALUE ? data.dimensionTwo : null,
            dimensionThreeItem:
              data.dimensionThree !== undefined && data.dimensionThree !== ALL_VALUE ? data.dimensionThree : null,
            dimensionFourItem:
              data.dimensionFour !== undefined && data.dimensionFour !== ALL_VALUE ? data.dimensionFour : null,
            dimensionFiveItem:
              data.dimensionFive !== undefined && data.dimensionFive !== ALL_VALUE ? data.dimensionFive : null,
          },
          assigneeId: data.assignee,
          period: {
            year: data.year,
            quarter: quarter?.value,
          },
          configurations: {
            directEmissionsShouldBeFilled: data.directEmissionsShouldBeFilled,
            indirectEmissionsShouldBeFilled: data.indirectEmissionsShouldBeFilled,
            purchasedGoodsAndServicesShouldBeFilled: data.purchasedGoodsAndServicesShouldBeFilled,
            capitalGoodsShouldBeFilled: data.capitalGoodsShouldBeFilled,
            fuelAndEnergyRelatedActivitiesShouldBeFilled: data.fuelAndEnergyRelatedActivitiesShouldBeFilled,
            upstreamTransportationAndDistributionShouldBeFilled:
              data.upstreamTransportationAndDistributionShouldBeFilled,
            wasteGeneratedInOperationsShouldBeFilled: data.wasteGeneratedInOperationsShouldBeFilled,
            businessTravelShouldBeFilled: data.businessTravelShouldBeFilled,
            employeeCommutingShouldBeFilled: data.employeeCommutingShouldBeFilled,
            upstreamLeasedAssetsShouldBeFilled: data.upstreamLeasedAssetsShouldBeFilled,
            downstreamTransportationAndDistributionShouldBeFilled:
              data.downstreamTransportationAndDistributionShouldBeFilled,
            processingOfSoldProductsShouldBeFilled: data.processingOfSoldProductsShouldBeFilled,
            useOfSoldProductsShouldBeFilled: data.useOfSoldProductsShouldBeFilled,
            treatmentOfSoldProductsShouldBeFilled: data.treatmentOfSoldProductsShouldBeFilled,
            downstreamLeasedAssetsShouldBeFilled: data.downstreamLeasedAssetsShouldBeFilled,
            franchisesShouldBeFilled: data.franchisesShouldBeFilled,
            investmentsShouldBeFilled: data.investmentsShouldBeFilled,
          },
        };
        if (!Object.values(dataToSave.configurations).some((configItem) => configItem === true)) {
          showErrorToast(toast, t("modal.missingSelectedSection"));
        } else {
          onSubmit(modalData?.id, dataToSave);
        }
      });
    }
  };

  const handleModalClose = () => {
    onClose();
  };

  const getModalHeaderText = () => {
    return modalData ? t("modal.editTaskHeader") : t("modal.createTaskHeader");
  };

  const renderDimensionOptions = (dimKey) => {
    let dimItems = [{ id: ALL_VALUE, name: "All" }];
    if (dimensionsData[dimKey] && dimensionsData[dimKey].items && dimensionsData[dimKey].items.length > 0) {
      dimItems.push(...dimensionsData[dimKey].items);
    }

    return dimItems
      .filter((item) => item.id === ALL_VALUE || item.state !== "ARCHIVED")
      .map((item) => {
        return (
          <option key={`${dimKey}-${item.id}`} value={item.id}>
            {item.name}
          </option>
        );
      });
  };

  console.log('dimension data => ',dimensionsData);

  return !isOpen || !organizationStructure || !dimensionsData || isUsersLoading ? null : (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <chakra.form onSubmit={handleSubmit(prepareSubmitData)}>
          <ModalHeader>{getModalHeaderText()}</ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <Heading size="md" marginBlockEnd="2">
              {t("modal.generalData")}
            </Heading>
            <Flex direction="column" gap="2">
              <FormTextArea label={t("modal.description")} type="text" errors={errors} {...register("description")} />

              <FormControl display="flex" alignItems="center" mt={4} mb={2}>
                <FormLabel htmlFor="use-template" mb="0">
                  {t("modal.useTemplate")}
                </FormLabel>
                <Switch
                  id="use-template"
                  isChecked={useTemplate}
                  onChange={(e) => {
                    setUseTemplate(e.target.checked);
                  }}
                />
              </FormControl>

              {useTemplate && (
                <FormSelect label={t("modal.selectTemplate")} onChange={handleTemplateChange} mb={4}>
                  <option value="">{t("modal.selectTemplateOption")}</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </FormSelect>
              )}

              {dimensionsKeys.map((dimKey) => {
                if (organizationStructure.dimensions[dimKey]) {
                  return (
                    <FormSelect
                      {...register(`${dimKey}`)}
                      key={`${dimKey}-select`}
                      label={`${organizationStructure.dimensions[dimKey].name} *`}
                      errors={errors}
                      disabled={(modalData && modalData.status !== QUESTIONNAIRE_STATUSES.NEW) || useTemplate}
                    >
                      {renderDimensionOptions(dimKey)}
                    </FormSelect>
                  );
                }
                return null;
              })}
              <FormSelect
                label={`${t("modal.assignee")} *`}
                placeholder=" "
                isDisabled={modalData != null || useTemplate}
                errors={errors}
                {...register("assignee", {
                  required: globalTranslation("entityRequired", { entityName: t("modal.assignee") }),
                })}
              >
                {users
                  ? users.map((assignee) => (
                      <option key={assignee.id} value={assignee.id}>
                        {assignee.email}
                      </option>
                    ))
                  : []}
              </FormSelect>
            </Flex>

            <Heading size="md" marginBlockStart="10" marginBlockEnd="2">
              {t("modal.selectScopes")}
            </Heading>

            <Flex direction="column" gap="2">
              <FormCheckBox
                label={`${t("modal.scope")} 1`}
                errors={errors}
                {...register("directEmissionsShouldBeFilled")}
                control={control}
              />
              <FormCheckBox
                label={`${t("modal.scope")} 2`}
                errors={errors}
                {...register("indirectEmissionsShouldBeFilled")}
                control={control}
              />

              <EsgLabel text="Scope 3" />
              <Stack pl={3} spacing={1}>
                <FormCheckBox
                  label={`${t("modal.category")} 1 - ${t(`modal.categories.purchasedGoodsAndServices`)}`}
                  errors={errors}
                  {...register("purchasedGoodsAndServicesShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 2 - ${t(`modal.categories.capitalGoods`)}`}
                  errors={errors}
                  {...register("capitalGoodsShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  isDisabled={true}
                  label={`${t("modal.category")} 3 - ${t(`modal.categories.fuelAndEnergyRelatedActivities`)}`}
                  errors={errors}
                  {...register("fuelAndEnergyRelatedActivitiesShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 4 - ${t(`modal.categories.upstreamTransportationAndDistribution`)}`}
                  errors={errors}
                  {...register("upstreamTransportationAndDistributionShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 5 - ${t(`modal.categories.wasteGeneratedInOperations`)}`}
                  errors={errors}
                  {...register("wasteGeneratedInOperationsShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 6 - ${t(`modal.categories.businessTravel`)}`}
                  errors={errors}
                  {...register("businessTravelShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 7 - ${t(`modal.categories.employeeCommuting`)}`}
                  errors={errors}
                  {...register("employeeCommutingShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  isDisabled={true}
                  label={`${t("modal.category")} 8 - ${t(`modal.categories.upstreamLeasedAssets`)}`}
                  errors={errors}
                  {...register("upstreamLeasedAssetsShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 9 - ${t(`modal.categories.downstreamTransportationAndDistribution`)}`}
                  errors={errors}
                  {...register("downstreamTransportationAndDistributionShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  isDisabled={true}
                  label={`${t("modal.category")} 10 - ${t(`modal.categories.processingOfSoldProducts`)}`}
                  errors={errors}
                  {...register("processingOfSoldProductsShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 11 - ${t(`modal.categories.useOfSoldProducts`)}`}
                  errors={errors}
                  {...register("useOfSoldProductsShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 12 - ${t(`modal.categories.treatmentOfSoldProducts`)}`}
                  errors={errors}
                  {...register("treatmentOfSoldProductsShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  isDisabled={true}
                  label={`${t("modal.category")} 13 - ${t(`modal.categories.downstreamLeasedAssets`)}`}
                  errors={errors}
                  {...register("downstreamLeasedAssetsShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 14 - ${t(`modal.categories.franchises`)}`}
                  errors={errors}
                  {...register("franchisesShouldBeFilled")}
                  control={control}
                />

                <FormCheckBox
                  label={`${t("modal.category")} 15 - ${t(`modal.categories.investments`)}`}
                  errors={errors}
                  {...register("investmentsShouldBeFilled")}
                  control={control}
                />
              </Stack>
            </Flex>
            <Heading size="md" marginBlockStart="10" marginBlockEnd="2">
              {t("modal.selectPeriod")}
            </Heading>
            <Flex direction="column" gap="2">
              <FormSelect
                label={`${t("modal.financialYear")} *`}
                type="text"
                errors={errors}
                {...register("year", {
                  required: globalTranslation("entityRequired", { entityName: t("modal.financialYear") }),
                })}
              >
                {generateArrayOfYears().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </FormSelect>

              <EsgFormLabel>{t("modal.quarter")} *</EsgFormLabel>
              <Controller
                control={control}
                name="quarter"
                render={({ field }) => (
                  <MultiSelect
                    options={[
                      { label: t("modal.quarterAll"), value: "ALL" },
                      { label: `${t("modal.quarterLetter")}1`, value: "Q1" },
                      { label: `${t("modal.quarterLetter")}2`, value: "Q2" },
                      { label: `${t("modal.quarterLetter")}3`, value: "Q3" },
                      { label: `${t("modal.quarterLetter")}4`, value: "Q4" },
                    ]}
                    placeholder={t("modal.quarter")}
                    value={field.value}
                    onChange={field.onChange}
                    isMulti={!modalData?.id}
                  />
                )}
              />
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>
              {t("modal.close")}
            </Button>
            <Button colorScheme="customScheme" type="submit">
              {t("modal.save")}
            </Button>
          </ModalFooter>
        </chakra.form>
      </ModalContent>
    </Modal>
  );
}

export default DataEntryPlanningModal;
